import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/utils/api";
import { fmtDateTime, ROLE_LABELS } from "@/utils/format";
import { USER } from "@/constants/testIds";
import { toast } from "sonner";
import { Plus, Pencil, ToggleLeft, ToggleRight, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { UserModal } from "@/components/users/UserModal";

const ROLE_COLORS = {
  admin: "bg-red-100 text-red-700 border-red-200",
  satis_destek: "bg-blue-100 text-blue-700 border-blue-200",
  muhasebe: "bg-purple-100 text-purple-700 border-purple-200",
  depo: "bg-amber-100 text-amber-700 border-amber-200",
  b2b_musteri: "bg-green-100 text-green-700 border-green-200",
};

export default function KullaniciYonetimi() {
  const [users, setUsers]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem]   = useState(null);
  const { user: currentUser } = useAuth();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const uRes = await api.get("/users/");
      setUsers(uRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleModalSuccess = useCallback(async (form, item) => {
    const payload = { ...form };
    if (!payload.cari_id) delete payload.cari_id;
    if (!payload.password) delete payload.password;
    if (item) {
      await api.put(`/users/${item.id}`, payload);
      toast.success("Kullanıcı güncellendi");
    } else {
      await api.post("/users/", payload);
      toast.success("Kullanıcı oluşturuldu");
    }
    fetchData();
  }, [fetchData]);

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "Pasifleştirildi" : "Aktifleştirildi");
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu kullanıcıyı silmek istediğinizden emin misiniz?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Kullanıcı silindi");
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0D1B3E]" style={{ fontFamily: "Outfit, sans-serif" }}>Kullanıcı Yönetimi</h1>
          <p className="text-sm text-slate-500 mt-0.5">{users.length} kullanıcı</p>
        </div>
        <button
          data-testid={USER.createButton}
          onClick={() => { setEditItem(null); setModalOpen(true); }}
          className="flex items-center gap-2 bg-[#0D1B3E] text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#172A58] active:scale-[0.98] transition-all"
        >
          <Plus size={15} /> Yeni Kullanıcı
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <table data-testid={USER.listTable} className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {["Ad Soyad", "E-Posta", "Rol", "Cari", "Durum", "Oluşturma", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Yükleniyor...</td></tr>
            ) : users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 font-medium text-slate-800">{u.name || "-"}</td>
                <td className="px-4 py-3 text-slate-600 text-xs">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${ROLE_COLORS[u.role] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
                    {ROLE_LABELS[u.role] || u.role}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500 font-mono">{u.cari_id ? u.cari_id.slice(-8) : "-"}</td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleActive(u)} disabled={u.id === currentUser?.id}
                    className="flex items-center gap-1.5 text-xs disabled:opacity-50">
                    {u.is_active
                      ? <><ToggleRight size={18} className="text-green-500" /><span className="text-green-600">Aktif</span></>
                      : <><ToggleLeft size={18} className="text-slate-400" /><span className="text-slate-500">Pasif</span></>
                    }
                  </button>
                </td>
                <td className="px-4 py-3 text-xs text-slate-400 tabular-nums">{fmtDateTime(u.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 justify-end">
                    <button onClick={() => { setEditItem(u); setModalOpen(true); }} className="text-slate-400 hover:text-blue-600 transition-colors"><Pencil size={14} /></button>
                    {u.id !== currentUser?.id && (
                      <button onClick={() => handleDelete(u.id)} className="text-slate-400 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <UserModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        editItem={editItem}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
}
